import * as Enumerable from 'linq';
import { PriorityUtility } from './priority.utility';
import { WorkOrderUtility } from './workorder.utility';
import { WorkOrderUpdateResponse } from '../responses';
import { SystemWorkOrderStatus } from '../entities/job/system.workorder.status';
import { WorkOrder } from '../entities/job/workorder';
import { WorkOrderStatus } from '../entities/job/workorder.status';
import { FilterFieldName, JobViewFilters, StatusFilterValue } from '../entities/job/job.filters';
import { Address } from '../entities/shared/address';
import { DefaultView, Job, JobDateSort, JobDisplay, JobGroupBy, JobSorting, JobSource, JobViewSummary, JobViewType, SortJobBy, WorkOrderStatusSystemName } from '../entities/job';
import { Priority } from '../entities/tenant/priority';
import { DateUtility } from './date.utility';
import { SitePro, SiteTiny } from '../entities';

export interface JobProcessingResponse {
  jobRes: {
    jobId: string;
    workOrderId: string;
    status: SystemWorkOrderStatus;
    workOrders?: WorkOrder[];
    completedDate?: Date;
    completedNote?: string;
    finalisedDate?: Date;
  };
  workorderRes: WorkOrderUpdateResponse[];
}

export interface RequestStatus {
  status: WorkOrderStatus;
  openStatus: WorkOrderStatus;
}

export interface WorkOrderLike {
  _id: string;
  status: WorkOrderStatus;
}

export class JobUtility {
  public static allowAutomaticUpdate(wo: WorkOrder) {
    if (wo.manuallyUpdated) return false;
    if (wo.status && (wo.status.systemStatus === 'Completed' || wo.status.systemStatus === 'Closed')) return false;

    return true;
  }

  public static buildJobStatus(workOrders: WorkOrderLike[]): SystemWorkOrderStatus {
    const wos = Enumerable.from(workOrders).where((e: WorkOrderLike) => e._id !== '0');

    if (wos.all((p: WorkOrderLike) => WorkOrderUtility.isSystemStatusClosed(p.status))) return SystemWorkOrderStatus.Closed;

    if (wos.all((p: WorkOrderLike) => WorkOrderUtility.isSystemStatusFinalised(p.status) || WorkOrderUtility.isSystemStatusClosed(p.status))) return SystemWorkOrderStatus.Finalised;

    if (wos.any((p: WorkOrderLike) => WorkOrderUtility.isSystemStatusOnHold(p.status))) return SystemWorkOrderStatus.OnHold;

    if (wos.any((p: WorkOrderLike) => WorkOrderUtility.isSystemStatusInProgress(p.status))) return SystemWorkOrderStatus.InProgress;

    if (wos.any((p: WorkOrderLike) => WorkOrderUtility.isSystemStatusOpen(p.status))) return SystemWorkOrderStatus.Open;

    if (wos.all((p: WorkOrderLike) => WorkOrderUtility.isSystemStatusCompleted(p.status) || WorkOrderUtility.isSystemStatusFinalised(p.status) || WorkOrderUtility.isSystemStatusClosed(p.status))) return SystemWorkOrderStatus.Completed;

    return SystemWorkOrderStatus.Open;
  }

  public static enableFilterField(fieldName: FilterFieldName, filters: JobViewFilters) {
    if (!filters) return false;

    switch (fieldName) {
      case FilterFieldName.Customer: {
        return !!filters.customers?.length;
      }
      case FilterFieldName.BillingCompanies: {
        return !!filters.billingCompanies?.length;
      }
      case FilterFieldName.Status: {
        return !!filters.statuses?.length;
      }
      case FilterFieldName.JobType: {
        return !!filters.jobTypes?.length;
      }
      case FilterFieldName.PriceBook: {
        return !!filters.priceBooks?.length;
      }
      case FilterFieldName.Priority: {
        return !!filters.priorities?.length;
      }
      case FilterFieldName.Owner: {
        return !!filters.owners?.length;
      }
      case FilterFieldName.FieldSupervisor: {
        return !!filters.fieldSupervisors?.length;
      }
      case FilterFieldName.Technician: {
        return !!filters.technicians?.length;
      }
      case FilterFieldName.Label: {
        return !!filters.labels?.length;
      }
      case FilterFieldName.Zones: {
        return !!filters.zones?.length;
      }
      case FilterFieldName.Resources: {
        return !!filters.resources?.length;
      }
      case FilterFieldName.WorkCategories: {
        return !!filters.workCategories?.length;
      }

      case FilterFieldName.CreatedAt:
      case FilterFieldName.DueDate:
      case FilterFieldName.CompletedDate:
      case FilterFieldName.FinalisedDate: {
        return true;
      }

    }
  }

  /// Used in job.sorting.dueDate
  public static getSortableDate(date?: Date): JobDateSort {
    return {
      asc: date ? date : new Date(2100, 12, 31, 0, 0),
      desc: date ? date : new Date(1980, 1, 1, 0, 0),
    };
  }

  /// Used in job.sorting.priority
  public static getSortablePriority(priority?: Priority): number {
    const sort = PriorityUtility.getSortOrder(priority);
    return sort;
  }

  public static getSortable(priority: Priority, dueDate: Date, completedDate: Date): JobSorting {
    return {
      priority: this.getSortablePriority(priority),
      dueDate: this.getSortableDate(dueDate),
      completedDate: this.getSortableDate(completedDate)
    } as JobSorting;
  }

  public static address(address: Address): string {
    if (!address) return '';

    if (address.name)
      return address.name;

    const street1 = address.street1 ? address.street1.trim() : '';
    const street2 = address.street2 ? address.street2.trim() : '';
    const suburb = address.suburb ? address.suburb.trim() : '';

    const state = address?.state?.trim() ?? '';
    const county = address?.county?.trim() ?? '';

    const postcode = address.postcode
      ? address.postcode?.toString().trim()
      : '';

    const postalCode = address?.postalCode?.trim() ?? '';

    const safePostalCode = postalCode
      ? postalCode
      : postcode;

    const components = [];
    if (street2) components.push(street2);
    if (street1) components.push(street1);
    if (suburb) components.push(suburb);
    if (county) components.push(county);
    if (state) components.push(state);
    if (safePostalCode) components.push(safePostalCode);

    return components.join(' ')?.trim();

    //return `${street1} ${suburb}`.trim() + ' ' + `${state} ${postcode}`.trim();
  }

  public static getAddress1(address: Address): string {
    if (address == null) return '';

    //hack for street standarising where rubbish is put into street 1
    //and the correct details are are in street 2
    let addressLine1 = (address?.street2 ? address.street2 : address.street1) ?? '';

    return addressLine1;
  }

  public static getAddress2(address: Address): string {

    if (address == null) return '';

    const components = [];

    const postCode = address?.postcode?.toString()?.trim();
    const postalCode = address?.postalCode?.trim();
    const safePostalCode = postalCode
      ? postalCode
      : postCode;

    if (address.suburb) components.push(address?.suburb?.trim());
    // if (address.state) components.push(address?.state?.trim());
    if (address.state) components.push(address?.state?.trim());
    if (safePostalCode) components.push(safePostalCode);
    if (address.country) components.push(address.country);

    //address2 = [address.suburb ? address.suburb : '', address.state ? address.state : '', address.postcode ? address.postcode : '', address.country ? address.country : ''].join(' ').trim();
    const address2 = components.join(' ').trim();

    return address2;
  }

  public static getSiteAddress(item: SitePro | SiteTiny): string {
    const addressName = item?.address?.name?.trim();
    const addressAutoName = JobUtility.address(item.address);
    return addressName || addressAutoName;
  }

  public static convertToJobResponse(job: Job): JobProcessingResponse {
    if (!job) return null;

    const dbWorkorders: WorkOrderUpdateResponse[] = [];

    Enumerable.from(job.workOrders).forEach((w) => {
      const workorder = {
        jobId: job._id,
        workOrderId: w._id,
        status: w.status,
      } as WorkOrderUpdateResponse;

      return dbWorkorders.push(workorder);
    });

    const dbJob = {
      jobId: job._id,
      workOrderId: job._id,
      status: job.status,
      workOrders: job.workOrders,
      completedDate: job.completedDate,
      completedNote: job.completedNote,
      finalisedDate: job.finalisedDate,
      updatedAt: job?.updatedAt,
    };

    return { jobRes: dbJob, workorderRes: dbWorkorders } as JobProcessingResponse;
  }

  public static createEmptyView(name = 'Not Configured'): JobViewSummary {

    return {
      _id: DefaultView.Empty,
      name,
      isDefault: true,
      system: true,
      selected: false,
      description: '',
      owner: null,
      source: JobSource.Active,
      filters: null
    } as JobViewSummary;

  }

  public static createDefaultViews(statuses: StatusFilterValue[]): JobViewSummary[] {
    const defaultViews = [];

    defaultViews.push({
      _id: DefaultView.Active,
      name: 'Active Jobs',
      isDefault: false,
      system: true,
      selected: false,
      description: '',
      owner: null,
      source: JobSource.Active,
      filters: {
        groupBy: JobGroupBy.Job,
        viewType: JobViewType.WorkOrder,
        displayType: JobDisplay.Kanban,
        sort: { sortBy: SortJobBy.CreatedDate, sortType: 'DESC' },
        statuses: Enumerable.from(statuses)
          .where((e) => {
            if (!e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.Open) return true;
            if (e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.InProgress) return true;
            if (e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.OnHold) return true;
            if (e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.Completed) return true;

            if (e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.Finalised) return true;
            return false;
          })
          .toArray(),
      },
    } as JobViewSummary);

    // defaultViews.push({
    //   _id: DefaultView.ActivePro,
    //   name: 'Active Jobs Pro',
    //   isDefault: false,
    //   system: true,
    //   selected: false,
    //   description: '',
    //   owner: null,
    //   source: JobSource.Active,
    //   filters: {
    //     groupBy: JobGroupBy.Job,
    //     viewType: JobViewType.WorkOrder,
    //     displayType: JobDisplay.Kanban,
    //     statuses: Enumerable.from(statuses)
    //       .where((e) => {
    //         if (!e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.Open) return true;
    //         if (e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.InProgress) return true;
    //         if (e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.OnHold) return true;
    //         if (e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.Completed) return true;

    //         if (e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.Finalised) return true;
    //         return false;
    //       })
    //       .toArray(),
    //   },
    // } as JobViewSummary);

    defaultViews.push({
      _id: DefaultView.Overdue,
      name: 'Overdue',
      isDefault: false,
      system: true,
      selected: false,
      description: '',
      owner: null,
      source: JobSource.Active,
      filters: {
        overDueDate: new Date(),
        groupBy: JobGroupBy.Job,
        viewType: JobViewType.WorkOrder,
        displayType: JobDisplay.Kanban,
        statuses: Enumerable.from(statuses)
          .where((e) => {
            if (!e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.Open) return true;
            if (e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.InProgress) return true;
            if (e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.OnHold) return true;
            return false;
          })
          .toArray(),
      } as JobViewFilters,
    } as JobViewSummary);

    defaultViews.push({
      _id: DefaultView.DueIn7Days,
      name: 'Due In Next 7 Days',
      isDefault: false,
      system: true,
      selected: false,
      description: '',
      owner: null,
      source: JobSource.Active,
      filters: {
        dueBeforeDate: DateUtility.getDateTime(new Date()).plus({ days: 7 }).endOf('day').toJSDate(),
        viewType: JobViewType.WorkOrder,
        groupBy: JobGroupBy.Job,
        displayType: JobDisplay.Kanban,
        sort: { sortBy: SortJobBy.CreatedDate, sortType: 'DESC' },
        statuses: Enumerable.from(statuses)
          .where((e) => {
            if (!e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.Open) return true;
            if (!e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.InProgress) return true;
            if (e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.OnHold) return true;
            return false;
          })
          .toArray(),
      } as JobViewFilters,
    } as JobViewSummary);

    defaultViews.push({
      _id: DefaultView.Unassigned,
      name: 'Not Assigned',
      isDefault: false,
      system: true,
      selected: false,
      description: '',
      owner: null,
      source: JobSource.Active,
      filters: {
        unassigned: true,
        viewType: JobViewType.WorkOrder,
        groupBy: JobGroupBy.Job,
        displayType: JobDisplay.List,
        sort: { sortBy: SortJobBy.CreatedDate, sortType: 'DESC' },
        statuses: Enumerable.from(statuses)
          .where((e) => e.systemName === WorkOrderStatusSystemName.New && !e.isSystemStatus)
          .toArray(),
      } as JobViewFilters,
    } as JobViewSummary);

    defaultViews.push({
      _id: DefaultView.NotDispatched,
      name: 'Not Dispatched',
      isDefault: false,
      system: true,
      selected: false,
      description: '',
      owner: null,
      source: JobSource.Active,
      sort: { sortBy: SortJobBy.CreatedDate, sortType: 'DESC' },
      filters: {
        statuses: Enumerable.from(statuses)
          .where((e) => e.systemName === WorkOrderStatusSystemName.Assigned)
          .toArray(),
        viewType: JobViewType.WorkOrder,
        groupBy: JobGroupBy.Job,
        displayType: JobDisplay.List,
      } as JobViewFilters,
    } as JobViewSummary);

    defaultViews.push({
      _id: DefaultView.Closed,
      name: 'Closed Jobs',
      isDefault: false,
      system: true,
      selected: false,
      description: '',
      owner: null,
      source: JobSource.Closed,
      sort: { sortBy: SortJobBy.CreatedDate, sortType: 'DESC' },
      filters: {
        displayType: JobDisplay.List,
        viewType: JobViewType.Job,
        statuses: Enumerable.from(statuses)
          .where((e) => (e.systemStatus === SystemWorkOrderStatus.Closed || e.systemStatus === SystemWorkOrderStatus.Canceled) && !e.isSystemStatus)
          .toArray(),
      },
    } as JobViewSummary);

    // defaultViews.push({
    //     _id: DefaultView.Archive,
    //     name: 'Archived Jobs',
    //     isDefault: false,
    //     system: true,
    //     selected: false,
    //     description: '',
    //     owner: null,
    //     source: JobSource.Archive,
    //     filters: {
    //         displayType: JobDisplay.List,
    //         viewType: JobViewType.Job,
    //         statuses: Enumerable
    //             .from(statuses)
    //             .where(e =>
    //                 (e.systemStatus === SystemWorkOrderStatus.Closed || e.systemStatus === SystemWorkOrderStatus.Canceled) && !e.isSystemStatus)
    //             .toArray()
    //     }
    // } as JobViewSummary);

    return defaultViews;
  }
}
